import React from 'react';
import {
  Grid,
  Checkbox,
  FormControlLabel,
  FormGroup,
} from '@material-ui/core';
import {
  Control, Controller, FieldValues, UseFormGetValues, UseFormSetValue, UseFormWatch, get,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Panel } from '@michelin/acid-components';
import { CSSProperties } from '@material-ui/styles';

export interface CheckBoxItem<T> {
  key: string,
  disabled?: boolean,
  enabledIf?: string[],
  style?: CSSProperties,
  notes?: string,
  onChange?: (fullData: T, fieldValue?: boolean) => void,
  onRender?: () => JSX.Element | null,
}

function isItemDisabled<T>(item:CheckBoxItem<T>, values:T) {
  if (item.disabled) return true;
  if (!item.enabledIf) return false;
  return !item.enabledIf.some((key) => (values as any)[key]);
}

interface CheckboxGroupProps<T extends FieldValues = FieldValues, L = any> {
  control: Control<T>,
  getValues: UseFormGetValues<T>,
  readOnly: boolean,
  items: CheckBoxItem<T>[],
  title?: string,
  labels: L,
  disableGroupSelector?: boolean,
  watch: UseFormWatch<T>,
  setValue: UseFormSetValue<T>,
  cols:4|6|12,
  style?: CSSProperties,
  shouldUnregister?: boolean
}

function createGroupSelector<T extends FieldValues = FieldValues>(fields: CheckBoxItem<T>[], readOnly: boolean, getValues: UseFormGetValues<T>, setValue: UseFormSetValue<T>) {
  let checked: number = 0;
  let unchecked: number = 0;
  const values = getValues() as any;
  fields.forEach((field) => {
    if (isItemDisabled(field, values)) return;
    if (!get(values, field.key as string)) unchecked++;
    else checked++;
  });
  if (checked === 0 && unchecked === 0) return null;
  return (
    <Checkbox
      checked={unchecked === 0}
      indeterminate={checked > 0 && unchecked > 0}
      disabled={readOnly}
      readOnly={readOnly}
      color="primary"
      onChange={(e, v) => {
        fields.forEach((field) => {
          if (field.disabled) return;
          setValue(field.key as any, v as any, { shouldDirty: true });
          if (field.onChange) field.onChange(values, v);
        });
      }}
    />
  );
}

export function CheckboxGroup<T extends FieldValues = FieldValues, L = any>({
  title, items, cols, readOnly, getValues, control, setValue, watch, labels, disableGroupSelector, style, shouldUnregister,
}:CheckboxGroupProps<T, L>) {
  const groupSelector = createGroupSelector(items, readOnly, getValues, setValue);
  const values = getValues() as T;
  const { t } = useTranslation();
  watch(items.map((f) => f.key) as any[]);

  const content = (
    <FormGroup>
      {!readOnly && !disableGroupSelector && groupSelector
        ? (
          <Grid container spacing={2} style={{ paddingLeft: 7 }}>
            <Grid item xs={12} md={6}>
              <FormControlLabel control={groupSelector} label={t('Select All')} />
            </Grid>
          </Grid>
        ) : null}
      <Grid container spacing={2} style={style || { paddingLeft: title ? 35 : undefined }}>
        {items.map((item) => (
          <Grid item xs={12} md={cols} style={item.style} key={`${item.key}-${get(values, item.key as string) ? '1' : '0'}` as string}>
            <FormControlLabel
              control={(
                <Controller
                  name={item.key as any}
                  control={control}
                  shouldUnregister={shouldUnregister}
                  render={(props) => (
                    <Checkbox
                      id={item.key as string}
                      checked={!!props.field.value}
                      onChange={(e) => {
                        if (readOnly || isItemDisabled(item, values)) return;
                        props.field.onChange(e.target.checked);
                        if (item.onChange) item.onChange(getValues() as T, e.target.checked);
                      }}
                      disabled={isItemDisabled(item, values)}
                      readOnly={readOnly}
                      color="primary"
                    />
                  )}
                />
              )}
              label={(
                <>
                  {t(get(labels, item.key as string))}
                  {item.notes && (
                  <div style={{ color: '#999999', fontSize: '0.8em' }}>
                    {t(item.notes)}
                  </div>
                  )}
                </>
                )}
            />
            {item.onRender && item?.onRender()}
          </Grid>
        ))}
      </Grid>
    </FormGroup>
  );

  if (!title) return content;

  return (
    <Panel title={t(title)} style={style}>
      {content}
    </Panel>
  );
}
